import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
const _excluded = ["addressId", "address", "propertyAddress"],
  _excluded2 = ["propertyAddress", "address"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { toJS } from 'mobx';
import { applySnapshot, getParent, getRoot, types } from 'mobx-state-tree';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { api } from 'src/api';
import { createAsync, createSilentAsync } from 'src/utils/asyncModel';
import { parseJwt } from 'src/core/services/jwt';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';
import { generateAddressString } from 'src/utils/address';
const companyFeatureStore = types.model('companyFeatureSet', {
  isAppfolio: false,
  convergentBillingMode: false,
  enableNewLayoutBeta: false,
  enableReimbursementReport: false,
  enableAllocationByNumberOfOccupants: false,
  disableFeeAssessmentMethod: false,
  disableReimbursementDetails: false,
  disableReports: false,
  disableDemoMode: false,
  disableEnrollResident: false,
  disablePostPayment: false,
  disableVoidStatement: false,
  disableLateFee: false,
  disableAddNewProperty: false,
  disableUnitOccupancyChange: false,
  disableRoommate: false,
  disableDeleteIncompletedProperty: false,
  disableAccountOverview: false,
  disableSendPaperInvite: false,
  disableSendReminder: false,
  disableSystemFee: false,
  disableStatementCap: false,
  disablePaperCorrespondence: false,
  disableBatchOccupancyChange: false,
  disableBatchSendReminder: false,
  disablePaperStatementFee: false,
  readOnlyLeaseDetails: false,
  readOnlyOccupancyChange: false,
  readOnlyScheduleMoveOut: false,
  readOnlyBillingStartDate: false,
  readOnlyPropertyUnitSetup: false,
  readOnlyMailingAddressUpdate: false,
  readOnlyPropertyPropertyInfo: false,
  readOnlyResidentPersonalDetails: false,
  readOnlyResidentPortalAccess: false
}).actions(store => ({
  setFeatureFlags(data) {
    try {
      Object.entries(data).forEach(_ref => {
        let _ref2 = _slicedToArray(_ref, 2),
          key = _ref2[0],
          value = _ref2[1];
        store["".concat(key)] = value;
      });
    } catch (error) {
      console.error('STORE ERROR > ', error);
    }
  }
}));
const createNewCompanyAsync = createAsync(body => {
  return async function createNewCompany(options) {
    const resp = await api.Companies.createNewCompany(body, options);
    localStorage.setItem(USER_SETTINGS_KEYS.company, resp.response);
    return resp;
  };
});
const getCompaniesAsync = createAsync(() => {
  return async function getCompanies(options) {
    const resp = await api.Companies.getCompanies(options);
    return resp;
  };
});
const getBillIntakeAliasAsync = createAsync(body => {
  return async function getBillIntakeAlias(options) {
    const resp = await api.Companies.getBillIntakeAlias(body, options);
    return resp;
  };
});

/**
 * Silent twin of the above, for the Bills page banner. The banner is additive: if the lookup fails
 * it renders nothing, so a toast would report an error about something the user never asked for.
 * `run()` swallows the rejection, so the call site cannot suppress the toast itself -- and non-400
 * toasts do not auto-close, so one failed lookup would follow the user across route changes.
 */
const getBillIntakeAliasSilentAsync = createSilentAsync(body => {
  return async function getBillIntakeAliasSilent(options) {
    const resp = await api.Companies.getBillIntakeAlias(body, options);
    return resp;
  };
});
const provisionBillIntakeAliasAsync = createAsync(body => {
  return async function provisionBillIntakeAlias(options) {
    const resp = await api.Companies.provisionBillIntakeAlias(body, options);
    return resp;
  };
});
const getBillIntakeAliasHistoryAsync = createAsync(body => {
  return async function getBillIntakeAliasHistory(options) {
    const resp = await api.Companies.getBillIntakeAliasHistory(body, options);
    return resp;
  };
});
const rotateBillIntakeAliasAsync = createAsync(body => {
  return async function rotateBillIntakeAlias(options) {
    const resp = await api.Companies.rotateBillIntakeAlias(body, options);
    return resp;
  };
});
const getCurrentUserCompanyFromTokenAsync = createAsync(() => {
  return async function getCompany(options) {
    var _parseJwt;
    const accessToken = localStorage.getItem(AUTH_TOKEN_KEYS.accessToken);
    const tokenCompanyId = accessToken && accessToken !== 'null' && accessToken !== 'undefined' ? (_parseJwt = parseJwt(accessToken)) === null || _parseJwt === void 0 ? void 0 : _parseJwt['custom:companyid'] : undefined;
    const storedCompanyId = localStorage.getItem(USER_SETTINGS_KEYS.company);
    const resolvedCompanyId = tokenCompanyId && tokenCompanyId !== 'undefined' && tokenCompanyId !== 'null' ? tokenCompanyId : storedCompanyId && storedCompanyId !== 'null' && storedCompanyId !== 'undefined' ? storedCompanyId : undefined;
    if (!!resolvedCompanyId && resolvedCompanyId !== 'undefined' && resolvedCompanyId !== 'null') {
      const homeDashboard = getRoot(this).homeDashboardStore;
      homeDashboard.ensureCompanyAccessIdMatchesSession(resolvedCompanyId);
      const resp = await api.Companies.getCompany({
        companyId: resolvedCompanyId
      }, options);
      if (!(resp !== null && resp !== void 0 && resp.isSuccessful) || !(resp !== null && resp !== void 0 && resp.response)) {
        return resp;
      }
      localStorage.setItem(USER_SETTINGS_KEYS.company, resolvedCompanyId);
      getRoot(this).admin.saveSelectedCompanyName(resp.response.name);
      getParent(this).company.companyFeatureStore.setFeatureFlags(resp.response.companySetting);
      getParent(this).company.setPropertyManagementSoftware(resp.response.propertyManagementSoftware);
      const exitingCompany = homeDashboard.getCompanyById(resolvedCompanyId);
      if (exitingCompany !== null) {
        exitingCompany.setCompanyName(resp.response.name);
        exitingCompany.companyFeatureSet.setFeatureFlags(resp.response.companySetting);
      }
      return resp;
    }
    return null;
  };
});
const getCompanyPropertiesAsync = createAsync(body => {
  return async function getCompanyProperties(options) {
    const companyId = localStorage.getItem(USER_SETTINGS_KEYS.company) || '';
    const featureFlagsStore = getParent(this).account.featureFlagsStore;

    // `properties-v2` names a utility after the newer utility-type join, which holds nothing for
    // utilities configured before bill automation, so callers that need the type ask for the
    // original endpoint instead — it reads the type off the utility itself.
    const useV2 = !(body !== null && body !== void 0 && body.withUtilityTypes) && (featureFlagsStore.getIsBillAutomationEnabled || featureFlagsStore.getIsBillingTemplatesEnabled);
    const resp = await api.Companies.getCompanyProperties({
      companyId
    }, options, useV2);
    const _getParent = getParent(this),
      company = _getParent.company;
    company.setCompanyPropertiesAfterLoad(resp);
    return resp;
  };
});
const getCompanyPropertiesForCompanyAsync = createAsync(body => {
  return async function getCompanyPropertiesForCompany(options) {
    const companyId = body.companyId;
    const resp = await api.Companies.getCompanyProperties({
      companyId
    }, options, getParent(this).account.featureFlagsStore.getIsBillAutomationEnabled || getParent(this).account.featureFlagsStore.getIsBillingTemplatesEnabled);
    return resp;
  };
});
const getCompanyPropertyListAsync = createAsync(() => {
  return async function getCompanyPropertyList(options) {
    const companyId = localStorage.getItem(USER_SETTINGS_KEYS.company) || '';
    const resp = await api.Companies.getCompanyPropertyList({
      companyId
    }, options);
    const _getParent2 = getParent(this),
      company = _getParent2.company;
    company.setCompanyPropertyListAfterLoad(resp);
    return resp;
  };
});
const getCompanyUnitsAsync = createAsync(body => {
  return async function getCompanyUnits(options) {
    const resp = await api.Companies.getCompanyUnits(body, options);
    const _getParent3 = getParent(this),
      company = _getParent3.company;
    company.setCompanyUnitsAmountAfterLoad(resp);
    return resp;
  };
});
const getResidentsAddendumsAsync = createAsync(() => {
  return async function getResidentsAddendums(options) {
    const companyId = localStorage.getItem(USER_SETTINGS_KEYS.company) || '';
    const resp = await api.Companies.getResidentsAddendums({
      companyId
    }, options);
    return resp;
  };
});
const updateCompanyAsync = createAsync(body => {
  return async function updateCompany(options) {
    const companyId = localStorage.getItem(USER_SETTINGS_KEYS.company) || '';
    const resp = await api.Companies.updateCompany(companyId, body, options);
    return resp;
  };
});
const RegisterNewCompany = types.model('registerNewCompany', {
  name: types.string,
  postalAddress: types.string,
  // website: types.string,
  propertyManagementSoftware: types.string,
  specifySoftware: types.union(types.string, types.undefined),
  unitsProjected: types.string,
  additionalSoftwareIsUsed: types.string
});
const Allocation = types.model('allocation', {
  allocationCharge: types.number,
  billStatus: types.string,
  billValue: types.number,
  buildingId: types.string,
  endDate: types.string,
  goodwillDeduction: types.number,
  startDate: types.string,
  utilityId: types.string
});
const CompanyBuildingsUtilities = types.model({
  allocation: types.optional(Allocation, {
    allocationCharge: 0,
    billStatus: '',
    billValue: 0,
    buildingId: '',
    endDate: '',
    goodwillDeduction: 0,
    startDate: '',
    utilityId: ''
  }),
  utilityId: types.string,
  utilityType: types.string
});
const Status = types.model('status', {
  PendingCompletion: types.optional(types.array(types.string), []),
  TableReady: types.optional(types.array(types.string), []),
  TableReadyStatementSent: types.optional(types.array(types.string), []),
  TableSkipped: types.optional(types.array(types.string), [])
});
const CompanyBuildings = types.model({
  propertyAddress: types.string,
  completedSteps: types.number,
  propertyId: types.identifier,
  propertyName: types.string,
  status: types.optional(Status, {
    PendingCompletion: [],
    TableReady: [],
    TableReadyStatementSent: [],
    TableSkipped: []
  }),
  utilities: types.optional(types.array(CompanyBuildingsUtilities), [])
});
const CompanyProperty = types.model('CompanyProperty', {
  propertyAddress: types.string,
  propertyId: types.string
  // propertyName: types.string,
});
const PropertyUtilities = types.model({
  allocation: types.optional(Allocation, {
    allocationCharge: 0,
    billStatus: '',
    billValue: 0,
    buildingId: '',
    endDate: '',
    goodwillDeduction: 0,
    startDate: '',
    utilityId: ''
  }),
  utilityId: types.string,
  // Tells apart two utilities of the same type on one property.
  utilityProviderName: types.optional(types.string, ''),
  utilityType: types.string
});
const Properties = types.model({
  propertyAddress: types.string,
  propertyId: types.identifier,
  propertyName: types.string,
  propertyStatus: types.string,
  utilities: types.optional(types.array(PropertyUtilities), [])
});
const CompanyStore = types.model('CompanyStore', {
  companyPropertyListValue: types.optional(types.array(CompanyProperty), []),
  getCompaniesAsync,
  getBillIntakeAliasAsync,
  getBillIntakeAliasSilentAsync,
  provisionBillIntakeAliasAsync,
  getBillIntakeAliasHistoryAsync,
  rotateBillIntakeAliasAsync,
  updateCompanyAsync,
  getCompanyUnitsAsync,
  createNewCompanyAsync,
  getCompanyPropertiesAsync,
  getCompanyPropertiesForCompanyAsync,
  getCompanyPropertyListAsync,
  getResidentsAddendumsAsync,
  getCurrentUserCompanyFromTokenAsync,
  propertyAddress: types.optional(types.string, ''),
  properties: types.optional(types.array(Properties), []),
  companyUnitsAmountValue: types.optional(types.number, 0),
  propertyIds: types.optional(types.array(types.string), []),
  companyBuildings: types.optional(types.array(CompanyBuildings), []),
  companyFeatureStore: types.optional(companyFeatureStore, {}),
  propertyManagementSoftware: types.optional(types.string, ''),
  registerNewCompany: types.optional(RegisterNewCompany, {
    name: '',
    postalAddress: '',
    // website: '',
    propertyManagementSoftware: '',
    specifySoftware: undefined,
    unitsProjected: '',
    additionalSoftwareIsUsed: 'no'
  })
}).actions(store => {
  return {
    setPropertyManagementSoftware(value) {
      store.propertyManagementSoftware = value !== null && value !== void 0 ? value : '';
    },
    setRegisterNewCompanyField(filed, value) {
      try {
        store.registerNewCompany[filed] = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    resetRegisterNewCompany() {
      try {
        store.registerNewCompany = {
          name: '',
          postalAddress: '',
          // website: '',
          propertyManagementSoftware: '',
          specifySoftware: undefined,
          unitsProjected: '',
          additionalSoftwareIsUsed: 'no'
        };
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setCompanyPropertiesAfterLoad(data) {
      try {
        applySnapshot(store, _objectSpread(_objectSpread({}, store), {}, {
          properties: data.response.map(d => {
            const addressId = d.addressId,
              address = d.address,
              propertyAddress = d.propertyAddress,
              rest = _objectWithoutProperties(d, _excluded);
            return _objectSpread({
              propertyAddress: generateAddressString(address)
            }, rest);
          }),
          propertyIds: data.response.map(b => b.propertyId)
        }));
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setCompanyPropertyListAfterLoad(data) {
      try {
        const currentData = [];
        data.response.map(i => {
          const propertyAddress = i.propertyAddress,
            address = i.address,
            rest = _objectWithoutProperties(i, _excluded2);
          currentData.push(_objectSpread(_objectSpread({}, rest), {}, {
            propertyAddress: generateAddressString(address)
          }));
        });
        applySnapshot(store, _objectSpread(_objectSpread({}, store), {}, {
          companyPropertyListValue: currentData
        }));
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setCompanyUnitsAmountAfterLoad(data) {
      try {
        store.companyUnitsAmountValue = data.response;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    }
  };
}).views(store => ({
  get companyPropertyList() {
    return toJS(store.companyPropertyListValue);
  },
  get companyUnitsAmount() {
    return store.companyUnitsAmountValue;
  },
  get propertiesList() {
    return toJS(store.properties);
  },
  getCompanyPropertyAddress(propertyId) {
    const property = store.companyPropertyListValue.find(p => p.propertyId === propertyId);
    return property ? property.propertyAddress : '';
  }
  // getCompanyPropertyName(propertyId: string) {
  //   const property = store.companyPropertyListValue.find(p => p.propertyId === propertyId);
  //   return property ? property.propertyName : '';
  // },
}));
export const company = types.optional(CompanyStore, {});